import { Form as FormikForm, Formik } from "formik";
import { useRef } from "react";
import { Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useViewport } from "../../contexts";
import {
    EditFormLifecycleActions,
    EditFormSaveCancelButtons,
    EditFormSplitFooter,
    shouldShowEditFormLifecycleActions,
} from "../../components/Forms";
import type { CustomerUpdatePayload } from "../../customTypings/customer/Customer";
import { customersService } from "../../services/customers/customers.service";
import {
    createEditFormSubmitHandler,
    DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE,
    arePayloadsEqual,
    getUserFacingErrorMessage,
    isUnchangedUpdateResponse,
    NO_CHANGES_TOAST_MESSAGE,
    showToastError,
    showToastSuccess,
    showToastUnchanged,
} from "../../utils";
import { formatCustomerToastLabel } from "../CustomersListPage/customerAdminListActions";
import { CustomerEditFormGrid } from "./CustomerEditFormGrid";
import { useCustomerEditOutletContext } from "./customerEditOutletContext";

type FormValues = { id?: string; name: string; isEnabled: boolean };

const validationSchema = Yup.object({
    id: Yup.string().optional(),
    name: Yup.string().trim().required("Name is required.").max(255, "Name must be at most 255 characters."),
    isEnabled: Yup.boolean().required(),
});

const CustomerEditFormView = () => {
    const navigate = useNavigate();
    const { width } = useViewport();
    const isNarrowScreen = width < 768;
    const { siteId, customerId, customerName, mainLocation, metaDeleted, metaEnabled, setConfirmKind } =
        useCustomerEditOutletContext();

    const savedUpdatePayloadRef = useRef<CustomerUpdatePayload | null>({
        id: customerId,
        name: customerName,
    });

    const buildUpdatePayload = (values: FormValues): CustomerUpdatePayload => ({
        id: values.id!,
        name: values.name.trim(),
    });

    return (
        <Formik<FormValues>
            enableReinitialize
            initialValues={{ id: customerId, name: customerName, isEnabled: metaEnabled }}
            validationSchema={validationSchema}
            validateOnMount={false}
            validateOnChange
            validateOnBlur
            onSubmit={async (values) => {
                if (metaDeleted) return;
                try {
                    const updatePayload = buildUpdatePayload(values);
                    if (arePayloadsEqual(updatePayload, savedUpdatePayloadRef.current)) {
                        showToastUnchanged(NO_CHANGES_TOAST_MESSAGE);
                        return;
                    }
                    const result = await customersService.update(siteId, updatePayload);
                    if (isUnchangedUpdateResponse(result)) {
                        showToastUnchanged(result.message ?? NO_CHANGES_TOAST_MESSAGE);
                        return;
                    }
                    showToastSuccess(`${formatCustomerToastLabel(values.name)} saved successfully!`);
                    navigate("/customers", { replace: true });
                } catch (err: unknown) {
                    showToastError(typeof err === "string" ? err : DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                }
            }}
        >
            {({ errors, touched, isSubmitting, handleSubmit, validateForm, setTouched, values }) => (
                <FormikForm
                    noValidate
                    onSubmit={createEditFormSubmitHandler(validateForm, setTouched, values, handleSubmit)}
                >
                    {metaDeleted && (
                        <Alert variant="warning" className="mb-3">
                            This customer is deleted. Restore it before you make any changes.
                        </Alert>
                    )}

                    <CustomerEditFormGrid
                        showSiteField={false}
                        isNew={false}
                        metaDeleted={metaDeleted}
                        siteId={siteId}
                        setSiteId={() => undefined}
                        siteDisplayName=""
                        sites={[]}
                        liveValidationAfterSubmit={false}
                        errors={errors}
                        touched={touched}
                        mainLocation={mainLocation}
                    />

                    <EditFormSplitFooter
                        start={
                            shouldShowEditFormLifecycleActions(false, values.id, isNarrowScreen) ? (
                                <div className="d-flex flex-wrap gap-2">
                                    <EditFormLifecycleActions
                                        metaDeleted={metaDeleted}
                                        metaEnabled={metaEnabled}
                                        onAction={setConfirmKind}
                                    />
                                </div>
                            ) : undefined
                        }
                    >
                        <EditFormSaveCancelButtons
                            isSubmitting={isSubmitting}
                            disabled={metaDeleted}
                            saveLabel="Save"
                            onCancel={() => navigate("/customers")}
                        />
                    </EditFormSplitFooter>
                </FormikForm>
            )}
        </Formik>
    );
};

export { CustomerEditFormView };
